import { useTheme } from "@emotion/react";
import { AppLinkTree } from "@megarax/links";
import { useState } from "react";
import { MdChevronRight } from "react-icons/md";
import { useLocation, useSearchParams } from "react-router-dom-v5-compat";

import { navAccent } from "./navConstants";
import { isAppTreeActive, isLinkExternal, isPageLinkActive, linkHref } from "./navHelpers";
import { NavLinkRow } from "./NavLinkRow";

type Props = {
  appTree: AppLinkTree;
  onNavigate?: () => void;
  keepSectionsExpanded?: boolean;
};

export const MegaraxNavSection: React.FC<Props> = ({ appTree, onNavigate, keepSectionsExpanded }) => {
  const theme = useTheme();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isActiveApp = isAppTreeActive(appTree, location.pathname, searchParams);
  const [isExpanded, setIsExpanded] = useState(isActiveApp);
  const isSectionExpanded = keepSectionsExpanded || isExpanded;

  if (appTree.pageLinks.length === 0) return null;

  const sectionColor = isActiveApp ? appTree.color : navAccent;

  const headerCss = {
    display: "flex",
    alignItems: "center",
    gap: "0.625rem",
    padding: "0.375rem 0.25rem",
    border: "none",
    background: "transparent",
    borderRadius: theme.smallBorderRadius,
    color: sectionColor,
    fontFamily: theme.displayFontFamily,
    fontWeight: isActiveApp ? 700 : 500,
    fontSize: "0.9375rem",
    textAlign: "left" as const,
    width: "100%",
  };

  const headerContent = (
    <>
      <appTree.Icon css={{ width: "1.125rem", height: "1.125rem", flexShrink: 0 }} />
      <span css={{ flex: 1 }}>{appTree.name}</span>
      {!keepSectionsExpanded && (
        <MdChevronRight
          css={{
            flexShrink: 0,
            width: "1.125rem",
            height: "1.125rem",
            opacity: 0.45,
            transition: "transform 0.15s ease",
            transform: isSectionExpanded ? "rotate(90deg)" : "rotate(0deg)",
          }}
        />
      )}
    </>
  );

  return (
    <div css={{ display: "flex", flexDirection: "column" }}>
      {keepSectionsExpanded ? (
        <div css={headerCss}>{headerContent}</div>
      ) : (
        <button
          type="button"
          onClick={() => setIsExpanded((prev) => !prev)}
          css={{
            ...headerCss,
            cursor: "pointer",
            "&:hover": { background: "rgba(0,0,0,0.03)" },
          }}
        >
          {headerContent}
        </button>
      )}

      {isSectionExpanded && (
        <div
          css={{
            display: "flex",
            flexDirection: "column",
            gap: "0.25rem",
            marginLeft: "0.625rem",
            paddingLeft: "0.875rem",
            marginTop: "0.125rem",
            marginBottom: "0.25rem",
            borderLeft: `2px solid ${isActiveApp ? appTree.color : theme.colors.skeleton}`,
          }}
        >
          {appTree.pageLinks.map((pageLink) => (
            <NavLinkRow
              key={pageLink.name}
              to={linkHref(pageLink.uri)}
              isExternal={isLinkExternal(pageLink.uri)}
              isActive={isPageLinkActive(pageLink, location.pathname, searchParams)}
              accentColor={isActiveApp ? appTree.color : undefined}
              icon={<pageLink.Icon css={{ width: "0.875rem", height: "0.875rem" }} />}
              onClick={onNavigate}
            >
              {pageLink.name}
            </NavLinkRow>
          ))}
        </div>
      )}
    </div>
  );
};
